import { useState, useEffect, useMemo } from "react";
import api from "@/services/api";
import {
  ExternalLink,
  FileText,
  Euro,
  Calendar,
  Target,
  MapPin,
  Clock,
  AlertCircle,
  XCircle,
  CheckCircle2,
  Timer,
  TrendingUp,
  Loader2,
  Copy,
  Link,
  Sparkles,
  AlertTriangle,
  Briefcase,
  FileSearch,
  Shield,
  Scale,
  ArrowRightCircle,
  Download,
  FileDown,
  Brain,
  Trophy,
  Swords,
  Zap,
  ClipboardCheck,
  MessageSquare,
  Plus,
  Trash2,
  PhoneCall,
  Mail,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import PerfilesBisturiDialog from "@/components/PerfilesBisturiDialog";

// Categoria config
const getCategoriaConfig = (cat) => {
  const configs = {
    alta_prioridad: { color: "bg-green-500", textColor: "text-green-400", label: "Alta Prioridad" },
    revisar: { color: "bg-yellow-500", textColor: "text-yellow-400", label: "Revisar" },
    baja_prioridad: { color: "bg-slate-500", textColor: "text-slate-400", label: "Baja Prioridad" },
  };
  return configs[cat] || configs.baja_prioridad;
};

// Estado labels
const estadoLabels = {
  nueva: "Nueva",
  en_estudio: "En Estudio",
  viable: "Viable",
  preparando_oferta: "Preparando Oferta",
  oferta_presentada: "Oferta Presentada",
  en_evaluacion: "En Evaluacion",
  adjudicada_srs: "Adjudicada SRS",
  no_adjudicada: "No Adjudicada",
  descartada: "Descartada",
  expirada: "Expirada",
};

// Tipo contrato
const tipoContratoLabels = {
  "1": "Obras",
  "2": "Servicios",
  "3": "Suministros",
  "21": "Gestion de servicios publicos",
  "31": "Concesion de obras",
};

// Recomendacion badge
const recomendacionConfig = {
  LICITAR: { bg: "bg-green-500/20", text: "text-green-400", label: "LICITAR" },
  LICITAR_CON_CONDICIONES: { bg: "bg-yellow-500/20", text: "text-yellow-400", label: "CON CONDICIONES" },
  NO_LICITAR: { bg: "bg-red-500/20", text: "text-red-400", label: "NO LICITAR" },
  ERROR: { bg: "bg-slate-500/20", text: "text-slate-400", label: "ERROR" },
};

// Guía de siguientes pasos por estado
const GUIA_PASOS = {
  nueva: "Revisar score y analisis IA. Si interesa, promover al pipeline.",
  en_estudio: "Descargar PPT/PCA. Verificar requisitos de solvencia.",
  viable: "Asignar responsable. Confirmar capacidad tecnica y equipo.",
  preparando_oferta: "Preparar documentacion. Revisar tesoros ocultos para ventaja competitiva.",
  oferta_presentada: "Registrar importe ofertado. Monitorizar resolucion en PLACSP.",
  en_evaluacion: "Esperar resolucion. Preparar plan B si es necesario.",
  adjudicada_srs: "Registrar datos de la adjudicacion para que el sistema aprenda.",
  no_adjudicada: "Registrar competidor ganador y motivo para afinar futuras licitaciones.",
};

const ESTADOS_CIERRE = ["adjudicada_srs", "no_adjudicada", "descartada"];

const activityTypes = [
  { value: "nota", label: "Nota", icon: MessageSquare },
  { value: "llamada", label: "Llamada", icon: PhoneCall },
  { value: "email", label: "Email", icon: Mail },
  { value: "reunion", label: "Reunion", icon: Calendar },
];

// Checklist de preparación de oferta
const CHECKLIST_ITEMS = [
  { key: "pliego_revisado", label: "Pliego descargado y revisado" },
  { key: "solvencia_verificada", label: "Requisitos de solvencia verificados" },
  { key: "ute_evaluada", label: "Necesidad de UTE evaluada" },
  { key: "memoria_tecnica", label: "Memoria tecnica preparada" },
  { key: "oferta_economica", label: "Oferta economica / presupuesto" },
  { key: "deuc_firmado", label: "DEUC firmado" },
  { key: "documentacion_revisada", label: "Documentacion completa revisada" },
  { key: "oferta_presentada", label: "Oferta presentada en PLACSP" },
];

const MOTIVOS_PERDIDA = [
  { value: "precio", label: "Precio" },
  { value: "tecnica", label: "Valoracion tecnica" },
  { value: "solvencia", label: "Falta de solvencia" },
  { value: "requisito_no_cumplido", label: "Requisito no cumplido" },
  { value: "otro", label: "Otro" },
];

const MOTIVOS_DESCARTE = [
  { value: "no_viable", label: "No viable" },
  { value: "sin_capacidad", label: "Sin capacidad" },
  { value: "plazo_insuficiente", label: "Plazo insuficiente" },
  { value: "presupuesto_bajo", label: "Presupuesto demasiado bajo" },
  { value: "otro", label: "Otro" },
];

export default function LicitacionModal({ open, onClose, licitacion, user }) {
  const [loading, setLoading] = useState(false);
  const [estado, setEstado] = useState("");
  const [notas, setNotas] = useState("");
  const [responsable, setResponsable] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [users, setUsers] = useState([]);

  // Checklist preparación oferta
  const [checklist, setChecklist] = useState({});

  // Actividades timeline
  const [activities, setActivities] = useState([]);
  const [newActivity, setNewActivity] = useState("");
  const [activityType, setActivityType] = useState("nota");

  // IA analysis
  const [analisis, setAnalisis] = useState(null);
  const [loadingAnalisis, setLoadingAnalisis] = useState(false);

  // v4.4.34 — Análisis Bisturí (8 dimensiones + viabilidad PYME por partner)
  const [bisturi, setBisturi] = useState(null);
  const [loadingBisturi, setLoadingBisturi] = useState(false);
  const [partnerBisturi, setPartnerBisturi] = useState("");
  const [perfilesBisturi, setPerfilesBisturi] = useState([]); // v4.4.35 — perfiles desde DB
  const [gestionarPerfiles, setGestionarPerfiles] = useState(false);
  const isAdmin = user?.role === "admin";

  // Feedback dialog (Nivel 2 Learning)
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackData, setFeedbackData] = useState({});

  // Aprendizajes (inteligencia histórica)
  const [aprendizajes, setAprendizajes] = useState(null);

  // Documentos (PCA/PPT)
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [docsData, setDocsData] = useState(null);
  const [docsLoaded, setDocsLoaded] = useState(false);

  const docLinks = useMemo(() => {
    const docs = docsData?.documents || licitacion?.documentos_disponibles || [];
    const pptUrl = docsData?.pliego_tecnico_url || licitacion?.pliego_tecnico_url || null;
    const findDoc = (...tipos) =>
      docs.find(d => tipos.includes(d.tipo) && (!d.formato || d.formato === "PDF"))?.url ||
      docs.find(d => tipos.includes(d.tipo))?.url || null;
    return {
      ppt: pptUrl || findDoc("pliego_tecnico"),
      pca: findDoc("pliego", "pliego_administrativo"),
      placsp: licitacion?.url_licitacion ||
        (licitacion?.expediente
          ? `https://contrataciondelestado.es/wps/poc?uri=deeplink:perfilContratante_busquedaLicitaciones&numExpContrato=${encodeURIComponent(licitacion.expediente)}`
          : null),
      all: docs,
    };
  }, [docsData, licitacion]);

  useEffect(() => {
    if (open && licitacion) {
      setEstado(licitacion.estado || "nueva");
      setNotas(licitacion.notas_analista || "");
      setResponsable(licitacion.responsable || "");
      setHasChanges(false);
      setChecklist(licitacion.checklist_oferta || {});
      setAnalisis(licitacion.analisis_pliego || null);
      setBisturi(licitacion.analisis_bisturi || null);
      setPartnerBisturi("");
      fetchPerfilesBisturi();
      setDocsData(null);
      const yaHayDocs = !!licitacion?.documentos_disponibles?.length;
      setDocsLoaded(yaHayDocs);
      fetchUsers();

      // Auto-fetch documentos en background (como OportunidadModal)
      if (!yaHayDocs && licitacion.expediente) {
        setTimeout(() => fetchDocumentos(true), 300);
      }

      // Actividades
      setActivities([]);
      setNewActivity("");
      setActivityType("nota");
      fetchActividades(licitacion._id);

      // Fetch aprendizajes (inteligencia histórica)
      setAprendizajes(null);
      setFeedbackOpen(false);
      setFeedbackData({});
      fetchAprendizajes(licitacion._id);
    }
  }, [open, licitacion]);

  // v4.4.35 — perfiles Bisturí desde DB (SRS + clientes externos)
  const fetchPerfilesBisturi = async () => {
    try {
      const response = await api.get('/perfiles-bisturi');
      setPerfilesBisturi(response.data?.perfiles || []);
    } catch (error) {
      console.error("Error fetching perfiles Bisturí:", error);
      setPerfilesBisturi([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Error cargando usuarios");
    }
  };

  const fetchAprendizajes = async (id) => {
    try {
      const resp = await api.get(`/licitaciones/${id}/aprendizajes`);
      if (resp.data.tiene_datos) setAprendizajes(resp.data);
    } catch (e) {
      // Silencioso — aprendizajes es nice-to-have
    }
  };

  // --- Actividades ---
  const fetchActividades = async (id) => {
    try {
      const resp = await api.get(`/licitaciones/${id}/actividades`);
      setActivities(resp.data);
    } catch (e) {
      console.error("Error fetching actividades:", e);
      toast.error("Error cargando actividades");
    }
  };

  const handleAddActivity = async () => {
    if (!newActivity.trim() || !licitacion) return;
    try {
      await api.post(
        `/licitaciones/${licitacion._id}/actividades`,
        { tipo: activityType, descripcion: newActivity }
      );
      toast.success("Actividad añadida");
      setNewActivity("");
      fetchActividades(licitacion._id);
    } catch (e) {
      toast.error("Error al añadir actividad");
    }
  };

  const handleDeleteActivity = async (activityId) => {
    if (!licitacion) return;
    try {
      await api.delete(
        `/licitaciones/${licitacion._id}/actividades/${activityId}`
      );
      setActivities((prev) => prev.filter((a) => a.activity_id !== activityId));
      toast.success("Actividad eliminada");
    } catch (e) {
      toast.error("Error al eliminar actividad");
    }
  };

  const handleSave = async (feedbackOverride) => {
    if (!licitacion) return;

    // Si es estado de cierre y no hay feedback aún, abrir dialog
    if (ESTADOS_CIERRE.includes(estado) && !feedbackOverride && !feedbackOpen) {
      setFeedbackOpen(true);
      return;
    }

    setLoading(true);
    try {
      const payload = { estado, notas_analista: notas, responsable: responsable || "", checklist_oferta: checklist };
      if (feedbackOverride) payload.feedback = feedbackOverride;

      await api.put(`/licitaciones/${licitacion._id}`, payload);
      toast.success("Licitacion actualizada");
      setHasChanges(false);
      setFeedbackOpen(false);
      onClose();
    } catch (error) {
      toast.error("Error al actualizar");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const submitFeedback = () => {
    const resultado = estado === "adjudicada_srs" ? "ganada" : estado === "no_adjudicada" ? "perdida" : "descartada";
    handleSave({ resultado, ...feedbackData });
  };

  const skipFeedback = () => {
    const resultado = estado === "adjudicada_srs" ? "ganada" : estado === "no_adjudicada" ? "perdida" : "descartada";
    handleSave({ resultado });
  };

  const fetchDocumentos = async (silent = false) => {
    if (!licitacion) return;
    setLoadingDocs(true);
    try {
      await api.post(
        `/licitaciones/${licitacion._id}/documentos/fetch`,
        {}
      );
      const resp = await api.get(
        `/licitaciones/${licitacion._id}/documentos`
      );
      setDocsData(resp.data);
      setDocsLoaded(true);
      if (!silent) {
        const numDocs = resp.data?.documents?.length || 0;
        if (numDocs > 0) {
          toast.success(`${numDocs} documentos obtenidos del expediente`);
        } else {
          toast.info("No se encontraron documentos en PLACSP para este expediente");
        }
      }
    } catch (error) {
      if (!silent) toast.error("Error obteniendo documentos");
      console.error(error);
    } finally {
      setLoadingDocs(false);
    }
  };

  const runAnalisis = async () => {
    if (!licitacion) return;
    setLoadingAnalisis(true);
    try {
      const resp = await api.post(
        `/licitaciones/${licitacion._id}/analizar`,
        {}
      );
      setAnalisis(resp.data.analisis);
      const rec = resp.data.analisis?.recomendacion || "?";
      toast.success(`Analisis IA: ${rec} via ${resp.data.analisis?.provider || "IA"}`);
    } catch (error) {
      toast.error("Error en analisis IA");
      console.error(error);
    } finally {
      setLoadingAnalisis(false);
    }
  };

  // v4.4.34 — Análisis Bisturí (8 dimensiones + viabilidad por partner)
  const runBisturi = async () => {
    if (!licitacion) return;
    setLoadingBisturi(true);
    try {
      const params = partnerBisturi ? `?partner=${partnerBisturi}` : "";
      const resp = await api.post(
        `/licitaciones/${licitacion._id}/analizar-bisturi${params}`,
        {}
      );
      const result = resp.data?.analisis;
      setBisturi(result);
      if (result?.error) {
        toast.warning(`Bisturí: ${result.error}`);
      } else {
        const nivel = result?.viabilidad_pyme?.nivel || "?";
        toast.success(`Análisis Bisturí: ${nivel.replace(/_/g, " ")}`);
      }
    } catch (error) {
      toast.error("Error en análisis Bisturí");
      console.error(error);
    } finally {
      setLoadingBisturi(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setHasChanges(true);
    if (field === "estado") setEstado(value);
    if (field === "notas") setNotas(value);
    if (field === "responsable") setResponsable(value);
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado`);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success(`${label} copiado`);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return "-";
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getDiasRestantes = (fechaLimite) => {
    if (!fechaLimite) return null;
    const diff = Math.ceil((new Date(fechaLimite) - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (!licitacion) return null;

  const catConfig = getCategoriaConfig(licitacion.categoria);
  const dias = getDiasRestantes(licitacion.fecha_limite_presentacion);
  const detalle = licitacion.score_detalle || {};

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 w-[95vw] max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <DialogHeader className="pb-4 border-b border-slate-800 pr-10">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-2">
                <DialogTitle className="text-lg font-bold text-white leading-tight line-clamp-2 flex-1">
                  {licitacion.objeto_contrato || "Sin titulo"}
                </DialogTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 h-8 w-8 text-slate-400 hover:text-white hover:bg-slate-700"
                  title="Copiar enlace"
                  onClick={() => {
                    const id = licitacion.expediente_id || licitacion._id || licitacion.id;
                    const url = `${window.location.origin}/licitaciones?open=${id}`;
                    const presupuesto = licitacion.presupuesto_base
                      ? new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(licitacion.presupuesto_base)
                      : "";
                    const plazo = licitacion.fecha_fin_presentacion
                      ? new Date(licitacion.fecha_fin_presentacion).toLocaleDateString("es-ES")
                      : "";
                    const lines = [`📋 LIC | ${(licitacion.objeto_contrato || "").substring(0, 80)}`];
                    if (presupuesto) lines.push(`💰 ${presupuesto}`);
                    if (plazo) lines.push(`⏰ Plazo: ${plazo}`);
                    lines.push(`🔗 ${url}`);
                    navigator.clipboard.writeText(lines.join("\n"));
                    toast.success("Enlace copiado");
                  }}
                >
                  <Link className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Badge className={`${catConfig.color} text-white font-bold`}>
                  {licitacion.score_total} pts
                </Badge>
                <Badge className={`${catConfig.color}/20 ${catConfig.textColor} border-0`}>
                  {catConfig.label}
                </Badge>
                {licitacion.tipo_contrato && (
                  <Badge variant="outline" className="border-purple-500/50 text-purple-400">
                    {tipoContratoLabels[licitacion.tipo_contrato] || licitacion.tipo_contrato}
                  </Badge>
                )}
                {licitacion.fondos_ue && (
                  <Badge className="bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                    <Euro className="w-3 h-3 mr-1" />
                    Fondos UE{licitacion.fondos_ue !== "Fondos Europeos" ? `: ${licitacion.fondos_ue}` : ""}
                  </Badge>
                )}
                {dias !== null && (
                  <Badge
                    className={`border-0 ${
                      dias <= 0
                        ? "bg-red-500/20 text-red-400"
                        : dias <= 7
                        ? "bg-red-500/15 text-red-400"
                        : dias <= 15
                        ? "bg-orange-500/20 text-orange-400"
                        : dias <= 30
                        ? "bg-yellow-500/20 text-yellow-400"
                        : "bg-green-500/20 text-green-400"
                    }`}
                  >
                    <Timer className="w-3 h-3 mr-1" />
                    {dias <= 0 ? "Vencida" : `${dias} dias`}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto py-4">
          <Tabs defaultValue="expediente" className="w-full">
            <TabsList className="bg-slate-800/50 mb-4 grid grid-cols-4 w-full">
              <TabsTrigger value="expediente" className="data-[state=active]:bg-green-500/20">
                <FileText className="w-4 h-4 mr-2" />
                Expediente
              </TabsTrigger>
              <TabsTrigger value="scoring" className="data-[state=active]:bg-green-500/20">
                <Target className="w-4 h-4 mr-2" />
                Scoring
              </TabsTrigger>
              <TabsTrigger value="analisis" className="data-[state=active]:bg-green-500/20">
                <Sparkles className="w-4 h-4 mr-2" />
                Analisis IA
              </TabsTrigger>
              <TabsTrigger value="gestion" className="data-[state=active]:bg-green-500/20">
                <Briefcase className="w-4 h-4 mr-2" />
                Gestion
              </TabsTrigger>
            </TabsList>

            {/* ===================== TAB: EXPEDIENTE ===================== */}
            <TabsContent value="expediente" className="space-y-4">
              {/* Identificacion */}
              <div className="p-4 bg-slate-800/50 rounded-lg space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-slate-500 text-xs">Expediente</Label>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-green-400 font-bold text-sm">
                        {licitacion.expediente_id || licitacion.expediente}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-slate-400 hover:text-white"
                        onClick={() => copyToClipboard(licitacion.expediente_id || licitacion.expediente, "Expediente")}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                    {licitacion.expediente_id && (
                      <p className="font-mono text-[10px] text-slate-500">
                        PLACSP: {licitacion.expediente}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-slate-500 text-xs">Estado PLACSP</Label>
                    <Badge variant="outline" className="border-slate-600 text-slate-300">
                      {licitacion.estado_placsp === "PUB"
                        ? "Publicada"
                        : licitacion.estado_placsp === "EV"
                        ? "En Evaluacion"
                        : licitacion.estado_placsp || "?"}
                    </Badge>
                  </div>
                </div>
                {docLinks.placsp && (
                  <a
                    href={docLinks.placsp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-400 hover:underline text-xs flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" /> Ver expediente en PLACSP
                  </a>
                )}
              </div>

              {/* Organo contratante */}
              <div className="p-3 bg-slate-800/30 rounded-lg space-y-1">
                <Label className="text-slate-500 text-xs">Organo de Contratacion</Label>
                <p className="text-white text-sm font-medium">
                  {licitacion.organo_contratacion || "No especificado"}
                </p>
              </div>

              {/* Datos clave */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-slate-500 text-xs">Presupuesto Base</Label>
                  <p className="text-xl font-bold text-green-400">
                    {formatCurrency(licitacion.presupuesto_base)}
                  </p>
                  {licitacion.valor_estimado && licitacion.valor_estimado !== licitacion.presupuesto_base && (
                    <p className="text-xs text-slate-500">
                      V. estimado: {formatCurrency(licitacion.valor_estimado)}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <Label className="text-slate-500 text-xs">Fecha Limite Presentacion</Label>
                  <p className="text-white font-medium">
                    {formatDate(licitacion.fecha_limite_presentacion)}
                  </p>
                  {licitacion.hora_limite && (
                    <p className="text-xs text-slate-400">Hora: {licitacion.hora_limite}</p>
                  )}
                  {dias !== null && (
                    <p
                      className={`text-sm font-bold ${
                        dias <= 0 ? "text-red-400" : dias <= 7 ? "text-red-400" : dias <= 15 ? "text-orange-400" : "text-green-400"
                      }`}
                    >
                      {dias <= 0 ? "VENCIDA" : `${dias} dias restantes`}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <Label className="text-slate-500 text-xs">CPV Principal</Label>
                  <p className="text-white font-mono">{licitacion.cpv_principal || "-"}</p>
                  {licitacion.cpv_info?.desc && (
                    <p className="text-xs text-slate-400">{licitacion.cpv_info.desc}</p>
                  )}
                </div>

                <div className="space-y-1">
                  <Label className="text-slate-500 text-xs">Lugar de Ejecucion</Label>
                  <p className="text-white flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-slate-400" />
                    {licitacion.lugar_ejecucion || "-"}
                  </p>
                </div>

                <div className="space-y-1">
                  <Label className="text-slate-500 text-xs">Tipo Contrato</Label>
                  <p className="text-white">
                    {tipoContratoLabels[licitacion.tipo_contrato] || licitacion.tipo_contrato || "-"}
                  </p>
                </div>

                <div className="space-y-1">
                  <Label className="text-slate-500 text-xs">Fecha Publicacion</Label>
                  <p className="text-white">{formatDate(licitacion.fecha_publicacion)}</p>
                </div>
              </div>

              {/* CPV adicionales */}
              {licitacion.cpv_adicionales && licitacion.cpv_adicionales.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-slate-500 text-xs">CPVs Adicionales</Label>
                  <div className="flex flex-wrap gap-1">
                    {licitacion.cpv_adicionales.map((cpv, idx) => (
                      <Badge key={idx} variant="outline" className="border-slate-600 text-slate-300 text-xs">
                        {cpv}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Keywords */}
              {licitacion.keywords_detectados && licitacion.keywords_detectados.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-slate-500 text-xs">Keywords Detectados</Label>
                  <div className="flex flex-wrap gap-1">
                    {licitacion.keywords_detectados.map((kw, idx) => (
                      <Badge key={idx} variant="outline" className="border-green-500/30 text-green-400 text-xs">
                        {kw}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* ===================== TAB: SCORING ===================== */}
            <TabsContent value="scoring" className="space-y-4">
              {/* Score total */}
              <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                <p className="text-slate-500 text-xs uppercase tracking-wide mb-2">Score Total</p>
                <p className={`text-5xl font-bold ${catConfig.textColor}`}>
                  {licitacion.score_total}
                </p>
                <Badge className={`${catConfig.color}/20 ${catConfig.textColor} border-0 mt-2`}>
                  {catConfig.label}
                </Badge>
              </div>

              {/* 5 dimensiones */}
              <div className="grid grid-cols-5 gap-3">
                {[
                  { key: "d1_cpv", label: "CPV", max: 25, color: "cyan", icon: FileSearch },
                  { key: "d2_keywords", label: "Keywords", max: 35, color: "purple", icon: Target },
                  { key: "d3_escala", label: "Escala", max: 15, color: "green", icon: Euro },
                  { key: "d4_plazo", label: "Plazo", max: 10, color: "yellow", icon: Timer },
                  { key: "d5_capacidad", label: "Capacidad", max: 15, color: "orange", icon: Shield },
                ].map((dim) => {
                  const val = detalle[dim.key] || 0;
                  const pct = Math.round((val / dim.max) * 100);
                  const DimIcon = dim.icon;
                  return (
                    <div key={dim.key} className="p-3 bg-slate-800/30 rounded-lg text-center">
                      <DimIcon className={`w-5 h-5 mx-auto mb-2 text-${dim.color}-400`} />
                      <p className={`text-2xl font-bold text-${dim.color}-400`}>{val}</p>
                      <div className="w-full bg-slate-700 rounded-full h-1.5 mt-2">
                        <div
                          className={`bg-${dim.color}-500 h-1.5 rounded-full`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <p className="text-[10px] text-slate-500 mt-1">
                        {dim.label} ({val}/{dim.max})
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Pilar */}
              <div className="p-4 bg-slate-800/50 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500 text-xs">Pilar Principal</Label>
                    <p className="text-white font-medium mt-1">
                      {licitacion.pilar_principal || "No clasificado"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-xs">Pilares Secundarios</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {licitacion.pilares_secundarios && licitacion.pilares_secundarios.length > 0 ? (
                        licitacion.pilares_secundarios.map((p, idx) => (
                          <Badge key={idx} variant="outline" className="border-slate-600 text-slate-300 text-xs">
                            {p}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-slate-500 text-sm">-</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* CPV Info */}
              {licitacion.cpv_info && (
                <div className="p-3 bg-slate-800/30 rounded-lg">
                  <Label className="text-slate-500 text-xs">Match CPV</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-mono text-white">{licitacion.cpv_info.code || "-"}</span>
                    {licitacion.cpv_info.nivel_match && (
                      <Badge
                        className={`border-0 ${
                          licitacion.cpv_info.nivel_match === "directo"
                            ? "bg-green-500/20 text-green-400"
                            : licitacion.cpv_info.nivel_match === "prefijo"
                            ? "bg-yellow-500/20 text-yellow-400"
                            : "bg-slate-500/20 text-slate-400"
                        }`}
                      >
                        {licitacion.cpv_info.nivel_match}
                      </Badge>
                    )}
                  </div>
                  {licitacion.cpv_info.desc && (
                    <p className="text-xs text-slate-400 mt-1">{licitacion.cpv_info.desc}</p>
                  )}
                </div>
              )}
            </TabsContent>

            {/* ===================== TAB: ANALISIS IA ===================== */}
            <TabsContent value="analisis" className="space-y-4">
              {/* Documentos del expediente + botones PCA/PPT */}
              <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-white font-medium flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-400" />
                    Documentos del Expediente
                  </h4>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                    onClick={fetchDocumentos}
                    disabled={loadingDocs}
                  >
                    {loadingDocs ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-1" />
                    )}
                    {docsLoaded ? "Actualizar" : "Obtener Documentos"}
                  </Button>
                </div>

                {/* Botones rapidos PCA / PPT / PLACSP — v4.4.14 theme-aware */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className={
                      docLinks.ppt
                        ? "bg-emerald-500/15 text-emerald-500 border border-emerald-500/50 hover:bg-emerald-500/25"
                        : "theme-bg-tertiary theme-text-muted cursor-not-allowed opacity-60"
                    }
                    style={!docLinks.ppt ? { border: "1px solid var(--theme-border)" } : {}}
                    disabled={!docLinks.ppt}
                    onClick={() => docLinks.ppt && window.open(docLinks.ppt, "_blank")}
                  >
                    <FileDown className="w-4 h-4 mr-1" />
                    PPT (Pliego Tecnico)
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className={
                      docLinks.pca
                        ? "bg-blue-500/15 text-blue-500 border border-blue-500/50 hover:bg-blue-500/25"
                        : "theme-bg-tertiary theme-text-muted cursor-not-allowed opacity-60"
                    }
                    style={!docLinks.pca ? { border: "1px solid var(--theme-border)" } : {}}
                    disabled={!docLinks.pca}
                    onClick={() => docLinks.pca && window.open(docLinks.pca, "_blank")}
                  >
                    <FileDown className="w-4 h-4 mr-1" />
                    PCA (Clausulas Admin)
                  </Button>
                  {docLinks.placsp && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="theme-bg-tertiary theme-text hover:bg-cyan-500/10"
                      style={{ border: "1px solid var(--theme-border)" }}
                      onClick={() => window.open(docLinks.placsp, "_blank")}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      PLACSP
                    </Button>
                  )}
                </div>

                {!docsLoaded && docLinks.all.length === 0 && (
                  <p className="text-slate-500 text-sm text-center py-2">
                    Cargando documentos de PLACSP...
                  </p>
                )}
                {docsLoaded && docLinks.all.length === 0 && (
                  <p className="text-slate-500 text-sm text-center py-2">
                    No se encontraron documentos en PLACSP para este expediente
                  </p>
                )}
              </div>

              {/* ===== v4.4.34 — ANÁLISIS BISTURÍ (8 dimensiones citables) ===== */}
              <div className="p-4 bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border border-purple-500/30 rounded-lg space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <h4 className="text-white font-medium flex items-center gap-2">
                      <ClipboardCheck className="w-5 h-5 text-purple-400" />
                      Bisturí — viabilidad pre-oferta
                    </h4>
                    <p className="text-slate-400 text-sm mt-1">
                      Extrae 8 dimensiones del pliego (tareas, subrogación, criterios, ISO, solvencia…)
                      con fuente citada y calcula viabilidad por partner. No inventa: si falta un dato, lo dice.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={partnerBisturi || "none"} onValueChange={(v) => setPartnerBisturi(v === "none" ? "" : v)}>
                      <SelectTrigger className="w-[180px] h-9 bg-slate-900 border-slate-700 text-sm">
                        <SelectValue placeholder="Perfil" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700 max-h-72">
                        <SelectItem value="none">Genérico (sin perfil)</SelectItem>
                        {perfilesBisturi.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.nombre}{!p.es_srs ? " · cliente" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => setGestionarPerfiles(true)}
                        className="text-xs text-purple-400 hover:text-purple-300 underline whitespace-nowrap"
                      >
                        Gestionar
                      </button>
                    )}
                    <Button
                      className="bg-purple-600 hover:bg-purple-700"
                      onClick={runBisturi}
                      disabled={loadingBisturi}
                    >
                      {loadingBisturi ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ClipboardCheck className="w-4 h-4 mr-2" />
                      )}
                      {bisturi ? "Re-analizar" : "Analizar"}
                    </Button>
                  </div>
                </div>

                {/* Resultado Bisturí */}
                {bisturi?.error && (
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-amber-300 text-sm flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 shrink-0" />
                      {bisturi.error}
                    </p>
                  </div>
                )}

                {bisturi?.viabilidad_pyme && (
                  <BisturiResultado bisturi={bisturi} />
                )}
              </div>

              {/* Boton analizar */}
              <div className="p-4 bg-gradient-to-r from-green-500/10 to-cyan-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-white font-medium flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-green-400" />
                      Analisis IA de Licitacion
                    </h4>
                    <p className="text-slate-400 text-sm mt-1">
                      {docsLoaded && docLinks.all.length > 0
                        ? `Analiza con IA incluyendo contenido de ${docLinks.all.length} documento(s) del pliego.`
                        : "Evalua si SRS debe licitar: viabilidad tecnica, economica, riesgos y estrategia."
                      }
                    </p>
                  </div>
                  <Button
                    className="bg-green-600 hover:bg-green-700"
                    onClick={runAnalisis}
                    disabled={loadingAnalisis}
                  >
                    {loadingAnalisis ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    {analisis ? "Re-analizar" : "Analizar"}
                  </Button>
                </div>
              </div>

              {/* Panel Inteligencia SRS + Siguiente Paso */}
              {(aprendizajes || GUIA_PASOS[estado]) && (
                <div className="space-y-3">
                  {/* Guía de siguiente paso */}
                  {GUIA_PASOS[estado] && (
                    <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg flex items-start gap-2">
                      <Zap className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
                      <div>
                        <span className="text-cyan-400 text-xs font-bold uppercase">Siguiente paso</span>
                        <p className="text-slate-300 text-sm">{analisis?.siguiente_paso || GUIA_PASOS[estado]}</p>
                      </div>
                    </div>
                  )}

                  {/* Inteligencia histórica */}
                  {aprendizajes && (
                    <div className="p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
                      <h4 className="text-indigo-400 text-xs font-bold uppercase mb-2 flex items-center gap-2">
                        <Brain className="w-4 h-4" />
                        Inteligencia SRS — {aprendizajes.total_similares} licitaciones similares
                      </h4>
                      <div className="space-y-1.5 text-sm">
                        {aprendizajes.tasa_exito !== null && (
                          <div className="flex items-center gap-2">
                            <Trophy className="w-3.5 h-3.5 text-green-400" />
                            <span className="text-slate-300">
                              Tasa de exito: <strong className={aprendizajes.tasa_exito >= 50 ? "text-green-400" : "text-red-400"}>{aprendizajes.tasa_exito}%</strong>
                              {" "}({aprendizajes.ganadas}G / {aprendizajes.perdidas}P)
                            </span>
                          </div>
                        )}
                        {aprendizajes.competidores_frecuentes?.length > 0 && (
                          <div className="flex items-center gap-2">
                            <Swords className="w-3.5 h-3.5 text-orange-400" />
                            <span className="text-slate-300">
                              Competidores: {aprendizajes.competidores_frecuentes.map(c => `${c.nombre} (${c.veces}x)`).join(", ")}
                            </span>
                          </div>
                        )}
                        {aprendizajes.precio_medio_adjudicado && (
                          <div className="flex items-center gap-2">
                            <Euro className="w-3.5 h-3.5 text-yellow-400" />
                            <span className="text-slate-300">
                              Precio adjudicado medio: {new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(aprendizajes.precio_medio_adjudicado)}
                            </span>
                          </div>
                        )}
                        {aprendizajes.ute_stats && (
                          <div className="flex items-center gap-2">
                            <Target className="w-3.5 h-3.5 text-purple-400" />
                            <span className="text-slate-300">
                              UTE: {aprendizajes.ute_stats.con_ute} con, {aprendizajes.ute_stats.sin_ute} sin
                            </span>
                          </div>
                        )}
                        {aprendizajes.lecciones_recientes?.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-indigo-500/20">
                            <span className="text-indigo-400 text-xs font-medium">Lecciones anteriores:</span>
                            {aprendizajes.lecciones_recientes.map((l, i) => (
                              <p key={i} className="text-slate-400 text-xs mt-1 pl-2 border-l-2 border-indigo-500/30">{l}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {loadingAnalisis ? (
                <div className="p-6 bg-slate-800/30 border border-green-500/30 rounded-lg flex items-center justify-center gap-3">
                  <Loader2 className="w-6 h-6 animate-spin text-green-400" />
                  <span className="text-slate-400">Analizando licitacion con IA...</span>
                </div>
              ) : analisis ? (
                <>
                  {/* Recomendacion */}
                  <div
                    className={`p-4 rounded-lg border ${
                      analisis.recomendacion === "LICITAR"
                        ? "bg-green-500/10 border-green-500/30"
                        : analisis.recomendacion === "LICITAR_CON_CONDICIONES"
                        ? "bg-yellow-500/10 border-yellow-500/30"
                        : "bg-red-500/10 border-red-500/30"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Scale className="w-5 h-5 text-white" />
                        <span className="text-white font-medium">Recomendacion</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          className={`${recomendacionConfig[analisis.recomendacion]?.bg || ""} ${
                            recomendacionConfig[analisis.recomendacion]?.text || ""
                          } border-0 text-sm font-bold`}
                        >
                          {recomendacionConfig[analisis.recomendacion]?.label || analisis.recomendacion}
                        </Badge>
                        <span className="text-slate-400 text-sm">
                          Confianza: {analisis.confianza}/10
                        </span>
                      </div>
                    </div>
                    <p className="text-slate-300 text-sm">{analisis.razon}</p>
                    {analisis.provider && (
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge variant="outline" className="border-slate-600 text-slate-500 text-[10px]">
                          via {analisis.provider} ({analisis.tokens_used} tokens)
                        </Badge>
                        {analisis.docs_analizados > 0 && (
                          <Badge variant="outline" className="border-blue-600 text-blue-400 text-[10px]">
                            📄 {analisis.docs_analizados} docs analizados
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Resumen Ejecutivo */}
                  {analisis.resumen_ejecutivo && (
                    <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                      <h4 className="text-purple-400 text-xs font-medium uppercase mb-2 flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        Resumen Ejecutivo
                      </h4>
                      <p className="text-slate-300 text-sm whitespace-pre-line">{analisis.resumen_ejecutivo}</p>
                    </div>
                  )}

                  {/* Tesoros Ocultos */}
                  {analisis.tesoros_ocultos && analisis.tesoros_ocultos.length > 0 && (
                    <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <h4 className="text-yellow-400 text-xs font-medium uppercase mb-2">Tesoros Ocultos</h4>
                      <div className="space-y-2">
                        {analisis.tesoros_ocultos.map((t, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <Badge className={`mt-0.5 text-[10px] ${t.relevancia === 'ALTA' ? 'bg-red-500/20 text-red-400' :
                                t.relevancia === 'MEDIA' ? 'bg-yellow-500/20 text-yellow-400' :
                                  'bg-slate-500/20 text-slate-400'
                              }`}>{t.relevancia}</Badge>
                            <div>
                              <p className="text-white text-sm">{t.hallazgo}</p>
                              <p className="text-cyan-400 text-xs">{t.oportunidad_srs}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Requisitos */}
                  {analisis.requisitos && analisis.requisitos.length > 0 && (
                    <div className="p-4 bg-slate-800/50 rounded-lg">
                      <h4 className="text-slate-400 text-xs font-medium uppercase mb-2">Requisitos</h4>
                      <div className="space-y-1">
                        {analisis.requisitos.map((r, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <Badge className={`text-[10px] ${r.tipo === 'HARD' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                              }`}>{r.tipo}</Badge>
                            <span className="text-slate-300">{r.descripcion}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Viabilidad */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                      <p className="text-slate-500 text-xs uppercase mb-1">Viabilidad Tecnica</p>
                      <p
                        className={`text-3xl font-bold ${
                          analisis.viabilidad_tecnica >= 7
                            ? "text-green-400"
                            : analisis.viabilidad_tecnica >= 4
                            ? "text-yellow-400"
                            : "text-red-400"
                        }`}
                      >
                        {analisis.viabilidad_tecnica}/10
                      </p>
                    </div>
                    <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                      <p className="text-slate-500 text-xs uppercase mb-1">Viabilidad Economica</p>
                      <p
                        className={`text-3xl font-bold ${
                          analisis.viabilidad_economica >= 7
                            ? "text-green-400"
                            : analisis.viabilidad_economica >= 4
                            ? "text-yellow-400"
                            : "text-red-400"
                        }`}
                      >
                        {analisis.viabilidad_economica}/10
                      </p>
                    </div>
                    <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                      <p className="text-slate-500 text-xs uppercase mb-1">Complejidad Oferta</p>
                      <p
                        className={`text-3xl font-bold ${
                          analisis.complejidad_oferta >= 7
                            ? "text-red-400"
                            : analisis.complejidad_oferta >= 4
                            ? "text-yellow-400"
                            : "text-green-400"
                        }`}
                      >
                        {analisis.complejidad_oferta}/10
                      </p>
                    </div>
                  </div>

                  {/* Estrategia */}
                  {analisis.estrategia_recomendada && (
                    <div className="p-4 bg-slate-800/50 rounded-lg">
                      <Label className="text-slate-500 text-xs">Estrategia Recomendada</Label>
                      <p className="text-white text-sm mt-1">{analisis.estrategia_recomendada}</p>
                    </div>
                  )}

                  {/* Servicios + Riesgos */}
                  <div className="grid grid-cols-2 gap-3">
                    {analisis.servicios_aplicables && analisis.servicios_aplicables.length > 0 && (
                      <div className="p-3 bg-slate-800/50 rounded-lg">
                        <Label className="text-slate-500 text-xs">Servicios SRS Aplicables</Label>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {analisis.servicios_aplicables.map((s, idx) => (
                            <Badge key={idx} variant="outline" className="border-green-500/30 text-green-400 text-xs">
                              {s}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {analisis.riesgos_principales && analisis.riesgos_principales.length > 0 && (
                      <div className="p-3 bg-slate-800/50 rounded-lg">
                        <Label className="text-slate-500 text-xs">Riesgos Principales</Label>
                        <div className="space-y-1 mt-2">
                          {analisis.riesgos_principales.map((r, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <AlertTriangle className="w-3 h-3 text-orange-400 mt-0.5 shrink-0" />
                              <span className="text-slate-300 text-xs">{r}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Solvencia */}
                  {analisis.requisitos_solvencia && (
                    <div className="p-3 bg-slate-800/30 rounded-lg">
                      <Label className="text-slate-500 text-xs">Requisitos de Solvencia (estimado IA)</Label>
                      <p className="text-slate-300 text-sm mt-1">{analisis.requisitos_solvencia}</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="p-6 bg-slate-800/30 border border-dashed border-slate-700 rounded-lg text-center">
                  <Sparkles className="w-10 h-10 mx-auto text-slate-600 mb-3" />
                  <p className="text-slate-500">
                    Pulsa "Analizar" para obtener la recomendacion IA sobre esta licitacion.
                  </p>
                </div>
              )}
            </TabsContent>

            {/* ===================== TAB: GESTION ===================== */}
            <TabsContent value="gestion" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Estado */}
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Estado</Label>
                  <Select value={estado} onValueChange={(v) => handleFieldChange("estado", v)}>
                    <SelectTrigger className="bg-slate-950 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      {Object.entries(estadoLabels).map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Responsable */}
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Responsable</Label>
                  <Select
                    value={responsable || "none"}
                    onValueChange={(v) => handleFieldChange("responsable", v === "none" ? "" : v)}
                  >
                    <SelectTrigger className="bg-slate-950 border-slate-700">
                      <SelectValue placeholder="Sin asignar" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="none">Sin asignar</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.user_id} value={u.name}>
                          {u.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Notas */}
              <div className="space-y-2">
                <Label className="text-slate-400 text-xs">Notas del Analista</Label>
                <Textarea
                  value={notas}
                  onChange={(e) => handleFieldChange("notas", e.target.value)}
                  placeholder="Notas sobre esta licitacion..."
                  className="bg-slate-950 border-slate-700 min-h-[100px]"
                />
              </div>

              {/* Checklist Preparación Oferta */}
              {(() => {
                const completed = CHECKLIST_ITEMS.filter(i => checklist[i.key]).length;
                const total = CHECKLIST_ITEMS.length;
                const pct = Math.round((completed / total) * 100);
                const allDone = completed === total;
                return (
                  <div className={`p-4 rounded-lg border ${allDone ? "bg-green-500/10 border-green-500/30" : "bg-[var(--theme-bg-secondary)] border-[var(--theme-border)]"}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <ClipboardCheck className={`w-4 h-4 ${allDone ? "text-green-400" : "text-cyan-400"}`} />
                        <Label className="text-xs font-semibold text-[var(--theme-text)] uppercase tracking-wide">
                          Checklist Preparacion Oferta
                        </Label>
                      </div>
                      <span className={`text-xs font-bold ${allDone ? "text-green-400" : completed > 0 ? "text-cyan-400" : "text-[var(--theme-text-secondary)]"}`}>
                        {completed}/{total} {allDone && "✓"}
                      </span>
                    </div>
                    {/* Progress bar */}
                    <div className="w-full h-1.5 bg-slate-700/50 rounded-full mb-3 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${allDone ? "bg-green-500" : "bg-gradient-to-r from-cyan-500 to-blue-500"}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    {/* Items */}
                    <div className="space-y-2">
                      {CHECKLIST_ITEMS.map((item) => {
                        const checked = !!checklist[item.key];
                        return (
                          <label
                            key={item.key}
                            className="flex items-center gap-3 cursor-pointer group py-0.5"
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(val) => {
                                const updated = { ...checklist, [item.key]: !!val };
                                setChecklist(updated);
                                setHasChanges(true);
                              }}
                              className="border-slate-600 data-[state=checked]:bg-cyan-600 data-[state=checked]:border-cyan-600"
                            />
                            <span className={`text-sm transition-all ${checked ? "text-[var(--theme-text-secondary)] line-through" : "text-[var(--theme-text)] group-hover:text-cyan-400"}`}>
                              {item.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Actividades / Timeline */}
              <div className="space-y-3">
                <Label className="text-[var(--theme-text-secondary)] text-xs uppercase tracking-wide flex items-center gap-2">
                  <MessageSquare className="w-3.5 h-3.5" />
                  Actividad
                </Label>
                {/* Input */}
                <div className="flex gap-2">
                  <Select value={activityType} onValueChange={setActivityType}>
                    <SelectTrigger className="w-[110px] bg-[var(--theme-bg-secondary)] border-[var(--theme-border)] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--theme-bg-secondary)] border-[var(--theme-border)]">
                      {activityTypes.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={newActivity}
                    onChange={(e) => setNewActivity(e.target.value)}
                    placeholder="Añadir actividad..."
                    className="flex-1 bg-[var(--theme-bg-secondary)] border-[var(--theme-border)] text-sm"
                    onKeyDown={(e) => e.key === "Enter" && handleAddActivity()}
                  />
                  <Button
                    type="button"
                    onClick={handleAddActivity}
                    size="sm"
                    className="bg-cyan-600 hover:bg-cyan-700 px-3"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {/* Timeline */}
                <div className="max-h-[200px] overflow-y-auto space-y-2">
                  {activities.length === 0 ? (
                    <p className="text-[var(--theme-text-secondary)] text-sm text-center py-4 opacity-60">
                      Sin actividad registrada
                    </p>
                  ) : (
                    activities.map((act) => {
                      const typeConf = activityTypes.find((t) => t.value === act.tipo);
                      const Icon = typeConf?.icon || MessageSquare;
                      return (
                        <div
                          key={act.activity_id}
                          className="group flex gap-3 p-3 bg-[var(--theme-bg-secondary)] rounded-lg border border-[var(--theme-border)]"
                        >
                          <div className="w-7 h-7 rounded-full bg-cyan-500/15 flex items-center justify-center text-cyan-400 shrink-0">
                            <Icon className="w-3.5 h-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="font-medium text-[var(--theme-text)] text-xs">{act.user_name}</span>
                              <Badge variant="outline" className="text-[10px] border-[var(--theme-border)] text-[var(--theme-text-secondary)] px-1.5 py-0">
                                {typeConf?.label || act.tipo}
                              </Badge>
                            </div>
                            <p className="text-sm text-[var(--theme-text)]">{act.descripcion}</p>
                            <p className="text-[10px] text-[var(--theme-text-secondary)] mt-1">{formatDate(act.created_at)}</p>
                          </div>
                          <button
                            onClick={() => handleDeleteActivity(act.activity_id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-red-400 hover:text-red-300 self-start"
                            title="Eliminar"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Metadatos */}
              <div className="p-4 bg-slate-800/30 rounded-lg">
                <Label className="text-slate-500 text-xs">Metadatos</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                  <div>
                    <span className="text-slate-500">Ingesta:</span>{" "}
                    <span className="text-slate-300">{formatDate(licitacion.fecha_ingesta)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Actualizado:</span>{" "}
                    <span className="text-slate-300">{formatDate(licitacion.fecha_actualizacion)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Fuente:</span>{" "}
                    <span className="text-slate-300">{licitacion.fuente || "-"}</span>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Footer Actions */}
        <div className="pt-4 border-t border-[var(--theme-border)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-[var(--theme-text-secondary)] uppercase font-medium mr-2">Estado:</span>
            <Select value={estado} onValueChange={(v) => handleFieldChange("estado", v)}>
              <SelectTrigger className="w-[160px] bg-[var(--theme-bg-secondary)] border-[var(--theme-border)] h-8 text-xs text-[var(--theme-text)]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--theme-bg-secondary)] border-[var(--theme-border)]">
                {Object.entries(estadoLabels).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Pasar al Pipeline — solo visible si es "nueva" y no está ya en pipeline */}
            {estado === "nueva" && !licitacion.en_pipeline && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    await api.put(
                      `/licitaciones/${licitacion._id}`,
                      {
                        en_pipeline: true,
                        responsable: responsable || user?.name || "",
                        notas_analista: notas,
                      }
                    );
                    toast.success("Licitación promovida al pipeline");
                    onClose();
                  } catch (error) {
                    toast.error("Error al pasar al pipeline");
                  }
                }}
                className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 h-8 text-xs"
              >
                <ArrowRightCircle className="w-4 h-4 mr-1" />
                Pipeline
              </Button>
            )}

              {estado !== "descartada" && estado !== "adjudicada_srs" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEstado("descartada");
                    setHasChanges(true);
                    setFeedbackOpen(true);
                  }}
                  className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 h-8"
                >
                  <XCircle className="w-4 h-4 mr-1.5" />
                  Descartar
                </Button>
              )}
          </div>

          <div className="flex items-center gap-2">
            {docLinks.placsp && (
              <Button
                variant="outline"
                onClick={() => window.open(docLinks.placsp, "_blank")}
                className="border-[var(--theme-border)] text-[var(--theme-text)]"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Ver en PLACSP
              </Button>
            )}

            {hasChanges && (
              <Button
                onClick={() => handleSave()}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
              >
                Guardar
              </Button>
            )}
          </div>
        </div>

        {/* ══════ FEEDBACK DIALOG (Nivel 2 Learning) ══════ */}
        {feedbackOpen && (
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-[var(--theme-bg)] border border-[var(--theme-border)] rounded-xl p-6 max-w-md w-full space-y-4 shadow-2xl">
              <div className="flex items-center gap-2 mb-2">
                <Brain className="w-5 h-5 text-cyan-400" />
                <h3 className="text-lg font-bold text-[var(--theme-text)]">
                  {estado === "adjudicada_srs" ? "Adjudicada — Datos para aprender" :
                   estado === "no_adjudicada" ? "No adjudicada — Datos para mejorar" :
                   "Descartada — Feedback rapido"}
                </h3>
              </div>
              <p className="text-xs text-[var(--theme-text-secondary)]">
                Estos datos alimentan la inteligencia de SRS para futuras licitaciones similares.
              </p>

              {/* Campos segun estado */}
              {estado === "adjudicada_srs" && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">Importe ofertado</Label>
                      <input type="number" className="w-full mt-1 px-3 py-2 text-sm rounded-lg bg-[var(--theme-bg-secondary)] border border-[var(--theme-border)] text-[var(--theme-text)]"
                        placeholder="€" onChange={(e) => setFeedbackData(d => ({ ...d, importe_oferta_srs: parseFloat(e.target.value) || null }))} />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Importe adjudicado</Label>
                      <input type="number" className="w-full mt-1 px-3 py-2 text-sm rounded-lg bg-[var(--theme-bg-secondary)] border border-[var(--theme-border)] text-[var(--theme-text)]"
                        placeholder="€" onChange={(e) => setFeedbackData(d => ({ ...d, importe_adjudicado: parseFloat(e.target.value) || null }))} />
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Label className="text-xs text-slate-500">Requirio UTE?</Label>
                    <div className="flex gap-2">
                      <Button size="sm" variant={feedbackData.requirio_ute === true ? "default" : "outline"} className="h-7 text-xs"
                        onClick={() => setFeedbackData(d => ({ ...d, requirio_ute: true }))}>Si</Button>
                      <Button size="sm" variant={feedbackData.requirio_ute === false ? "default" : "outline"} className="h-7 text-xs"
                        onClick={() => setFeedbackData(d => ({ ...d, requirio_ute: false }))}>No</Button>
                    </div>
                  </div>
                  {feedbackData.requirio_ute && (
                    <div>
                      <Label className="text-xs text-slate-500">Partner UTE</Label>
                      <input className="w-full mt-1 px-3 py-2 text-sm rounded-lg bg-[var(--theme-bg-secondary)] border border-[var(--theme-border)] text-[var(--theme-text)]"
                        placeholder="Nombre del partner" onChange={(e) => setFeedbackData(d => ({ ...d, partner_ute: e.target.value }))} />
                    </div>
                  )}
                  <div>
                    <Label className="text-xs text-slate-500">Que nos ayudo a ganar?</Label>
                    <Textarea className="mt-1 text-sm bg-[var(--theme-bg-secondary)] border-[var(--theme-border)] text-[var(--theme-text)]" rows={2}
                      placeholder="Precio competitivo, experiencia previa, equipo tecnico..." onChange={(e) => setFeedbackData(d => ({ ...d, lecciones: e.target.value }))} />
                  </div>
                </div>
              )}

              {estado === "no_adjudicada" && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500">Quien gano?</Label>
                    <input className="w-full mt-1 px-3 py-2 text-sm rounded-lg bg-[var(--theme-bg-secondary)] border border-[var(--theme-border)] text-[var(--theme-text)]"
                      placeholder="Empresa ganadora" onChange={(e) => setFeedbackData(d => ({ ...d, competidor_ganador: e.target.value }))} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">Oferta SRS</Label>
                      <input type="number" className="w-full mt-1 px-3 py-2 text-sm rounded-lg bg-[var(--theme-bg-secondary)] border border-[var(--theme-border)] text-[var(--theme-text)]"
                        placeholder="€" onChange={(e) => setFeedbackData(d => ({ ...d, importe_oferta_srs: parseFloat(e.target.value) || null }))} />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Precio ganador</Label>
                      <input type="number" className="w-full mt-1 px-3 py-2 text-sm rounded-lg bg-[var(--theme-bg-secondary)] border border-[var(--theme-border)] text-[var(--theme-text)]"
                        placeholder="€" onChange={(e) => setFeedbackData(d => ({ ...d, importe_adjudicado: parseFloat(e.target.value) || null }))} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Por que perdimos?</Label>
                    <Select onValueChange={(v) => setFeedbackData(d => ({ ...d, motivo: v }))}>
                      <SelectTrigger className="mt-1 bg-[var(--theme-bg-secondary)] border-[var(--theme-border)] text-sm text-[var(--theme-text)]"><SelectValue placeholder="Seleccionar motivo" /></SelectTrigger>
                      <SelectContent className="bg-[var(--theme-bg-secondary)] border-[var(--theme-border)]">
                        {MOTIVOS_PERDIDA.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Lecciones aprendidas</Label>
                    <Textarea className="mt-1 text-sm bg-[var(--theme-bg-secondary)] border-[var(--theme-border)] text-[var(--theme-text)]" rows={2}
                      placeholder="Que hariamos diferente..." onChange={(e) => setFeedbackData(d => ({ ...d, lecciones: e.target.value }))} />
                  </div>
                </div>
              )}

              {estado === "descartada" && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500">Motivo de descarte</Label>
                    <Select onValueChange={(v) => setFeedbackData(d => ({ ...d, motivo: v }))}>
                      <SelectTrigger className="mt-1 bg-[var(--theme-bg-secondary)] border-[var(--theme-border)] text-sm text-[var(--theme-text)]"><SelectValue placeholder="Seleccionar motivo" /></SelectTrigger>
                      <SelectContent className="bg-[var(--theme-bg-secondary)] border-[var(--theme-border)]">
                        {MOTIVOS_DESCARTE.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Score feedback (comun a todos) */}
              <div>
                <Label className="text-xs text-slate-500">El score inicial fue acertado?</Label>
                <div className="flex gap-2 mt-1">
                  {["correcto", "demasiado_alto", "demasiado_bajo"].map(v => (
                    <Button key={v} size="sm" variant={feedbackData.score_feedback === v ? "default" : "outline"} className="h-7 text-xs"
                      onClick={() => setFeedbackData(d => ({ ...d, score_feedback: v }))}>
                      {v === "correcto" ? "Correcto" : v === "demasiado_alto" ? "Muy alto" : "Muy bajo"}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-[var(--theme-border)]">
                <Button variant="ghost" size="sm" onClick={() => setFeedbackOpen(false)} className="text-slate-500">Cancelar</Button>
                <Button variant="ghost" size="sm" onClick={skipFeedback} className="text-slate-400">Omitir</Button>
                <Button size="sm" onClick={submitFeedback} disabled={loading} className="bg-cyan-600 hover:bg-cyan-700 text-white">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar feedback"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      {isAdmin && (
        <PerfilesBisturiDialog
          open={gestionarPerfiles}
          onClose={() => setGestionarPerfiles(false)}
          onChange={fetchPerfilesBisturi}
        />
      )}
    </Dialog>
  );
}


// =====================================================================
// BisturiResultado — v4.4.34: renderiza las 8 dimensiones + viabilidad
// =====================================================================

const NIVEL_CONFIG = {
  VIABLE: {
    label: "VIABLE",
    className: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/50",
    icon: CheckCircle2,
  },
  RIESGO_MEDIO: {
    label: "RIESGO MEDIO",
    className: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/50",
    icon: AlertTriangle,
  },
  NO_VIABLE: {
    label: "NO VIABLE",
    className: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/50",
    icon: XCircle,
  },
  FUERA_SECTOR: {
    label: "FUERA DE SECTOR",
    className: "bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/50",
    icon: XCircle,
  },
};

function BisturiResultado({ bisturi }) {
  const a = bisturi.analisis_bisturi || {};
  const v = bisturi.viabilidad_pyme || {};
  const nivelCfg = NIVEL_CONFIG[v.nivel] || NIVEL_CONFIG.RIESGO_MEDIO;
  const NivelIcon = nivelCfg.icon;

  // Si es FUERA_SECTOR, mostramos solo el semáforo (no hay análisis de pliego)
  const esFueraSector = v.nivel === "FUERA_SECTOR";

  // Helper para mostrar fuente
  const Fuente = ({ src }) =>
    src && src !== "No se especifica en los documentos analizados" ? (
      <span className="text-[10px] theme-text-muted italic ml-1">({src})</span>
    ) : null;

  const subr = a.subrogacion || {};
  const crit = a.criterios_adjudicacion || {};
  const certs = a.certificaciones || {};
  const solv = a.solvencia || {};
  const carac = a.caracteristicas || {};
  const modal = a.modalidad || {};
  const tareas = a.tareas || {};

  return (
    <div className="space-y-3">
      {/* Semáforo de viabilidad */}
      <div className={`p-3 rounded-lg border ${nivelCfg.className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-bold">
            <NivelIcon className="w-5 h-5" />
            {nivelCfg.label}
            <span className="text-xs font-normal opacity-70">· score {v.score}/100</span>
          </div>
          {bisturi.llm_provider && (
            <span className="text-[10px] opacity-60">
              {bisturi.llm_provider} · {bisturi.docs_analizados} docs
            </span>
          )}
        </div>

        {/* Bloqueantes (rojo) */}
        {v.bloqueantes?.length > 0 && (
          <ul className="mt-2 space-y-1">
            {v.bloqueantes.map((b, i) => (
              <li key={i} className="text-xs flex items-start gap-1.5">
                <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-red-400" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}
        {/* Positivos */}
        {v.factores_positivos?.length > 0 && (
          <ul className="mt-2 space-y-1">
            {v.factores_positivos.map((p, i) => (
              <li key={i} className="text-xs flex items-start gap-1.5 text-emerald-700 dark:text-emerald-300/90">
                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        )}
        {/* Riesgos */}
        {v.factores_riesgo?.length > 0 && (
          <ul className="mt-2 space-y-1">
            {v.factores_riesgo.map((r, i) => (
              <li key={i} className="text-xs flex items-start gap-1.5 text-amber-700 dark:text-amber-300/90">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Dimensiones del pliego — solo si NO es fuera de sector */}
      {!esFueraSector && (<>
      {/* Grid de dimensiones clave */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
        {/* Criterios */}
        <div className="p-2.5 theme-bg-tertiary theme-border border rounded-lg">
          <div className="text-[10px] uppercase theme-text-muted flex items-center gap-1">
            <Scale className="w-3 h-3" /> Criterios adjudicación
          </div>
          <div className="theme-text mt-0.5">
            {crit.automaticos_pct != null ? `${crit.automaticos_pct}% automático` : "—"}
            {crit.subjetivos_pct != null ? ` · ${crit.subjetivos_pct}% subjetivo` : ""}
          </div>
          {crit.subjetivos_detalle?.length > 0 && (
            <div className="text-[11px] theme-text-muted mt-0.5">{crit.subjetivos_detalle.join(", ")}</div>
          )}
          <Fuente src={crit.fuente} />
        </div>

        {/* Subrogación */}
        <div className="p-2.5 theme-bg-tertiary theme-border border rounded-lg">
          <div className="text-[10px] uppercase theme-text-muted flex items-center gap-1">
            <Briefcase className="w-3 h-3" /> Subrogación
          </div>
          <div className="theme-text mt-0.5">
            {subr.obligatoria === true
              ? `Obligatoria${subr.num_trabajadores ? ` · ${subr.num_trabajadores} trab.` : ""}`
              : subr.obligatoria === false
              ? "No obligatoria"
              : "No especificada"}
          </div>
          <Fuente src={subr.fuente} />
        </div>

        {/* Certificaciones */}
        <div className="p-2.5 theme-bg-tertiary theme-border border rounded-lg">
          <div className="text-[10px] uppercase theme-text-muted flex items-center gap-1">
            <Shield className="w-3 h-3" /> Certificaciones ISO
          </div>
          <div className="theme-text mt-0.5">
            {certs.obligatorias?.length > 0 && (
              <span className="text-red-600 dark:text-red-300">
                Obligatorias: {certs.obligatorias.map((c) => c.norma).join(", ")}
              </span>
            )}
            {certs.valorables?.length > 0 && (
              <span className="text-emerald-600 dark:text-emerald-300 block">
                Valorables: {certs.valorables.map((c) => `${c.norma}${c.puntos ? ` (+${c.puntos})` : ""}`).join(", ")}
              </span>
            )}
            {!certs.obligatorias?.length && !certs.valorables?.length && "Ninguna especificada"}
          </div>
        </div>

        {/* Características */}
        <div className="p-2.5 theme-bg-tertiary theme-border border rounded-lg">
          <div className="text-[10px] uppercase theme-text-muted flex items-center gap-1">
            <FileText className="w-3 h-3" /> Contrato
          </div>
          <div className="theme-text mt-0.5">
            {carac.tipo_contrato || "—"}
            {carac.duracion_inicial ? ` · ${carac.duracion_inicial}` : ""}
            {modal.tipo ? ` · ${modal.tipo}` : ""}
          </div>
          {carac.prorrogas && carac.prorrogas !== "No se especifica en los documentos analizados" && (
            <div className="text-[11px] theme-text-muted mt-0.5">Prórrogas: {carac.prorrogas}</div>
          )}
        </div>
      </div>

      {/* Solvencia (texto literal) */}
      {(solv.economica_literal || solv.tecnica_literal) && (
        <div className="p-2.5 theme-bg-tertiary theme-border border rounded-lg space-y-2">
          <div className="text-[10px] uppercase theme-text-muted">Solvencia (literal del pliego)</div>
          {solv.economica_literal && solv.economica_literal !== "No se especifica en los documentos analizados" && (
            <div className="text-xs theme-text">
              <span className="theme-text-muted">Económica:</span> {solv.economica_literal}
              <Fuente src={solv.economica_fuente} />
            </div>
          )}
          {solv.tecnica_literal && solv.tecnica_literal !== "No se especifica en los documentos analizados" && (
            <div className="text-xs theme-text">
              <span className="theme-text-muted">Técnica:</span> {solv.tecnica_literal}
              <Fuente src={solv.tecnica_fuente} />
            </div>
          )}
        </div>
      )}

      {/* Tareas */}
      {tareas.resumen && (
        <div className="p-2.5 theme-bg-tertiary theme-border border rounded-lg">
          <div className="text-[10px] uppercase theme-text-muted flex items-center gap-1">
            <Target className="w-3 h-3" /> Objeto / tareas
          </div>
          <div className="text-xs theme-text mt-0.5">{tareas.resumen}</div>
          {tareas.plazos_ejecucion && tareas.plazos_ejecucion !== "No se especifica en los documentos analizados" && (
            <div className="text-[11px] theme-text-muted mt-0.5 flex items-center gap-1">
              <Clock className="w-3 h-3" /> {tareas.plazos_ejecucion}
            </div>
          )}
        </div>
      )}

      {/* Datos no encontrados (transparencia: no inventa) */}
      {a.datos_no_encontrados?.length > 0 && (
        <div className="p-2.5 theme-bg-tertiary border border-dashed theme-border rounded-lg">
          <div className="text-[10px] uppercase theme-text-muted mb-1">
            No encontrado en los documentos ({a.datos_no_encontrados.length})
          </div>
          <ul className="text-[11px] theme-text-muted space-y-0.5">
            {a.datos_no_encontrados.map((d, i) => (
              <li key={i}>· {d}</li>
            ))}
          </ul>
        </div>
      )}
      </>)}
    </div>
  );
}
